import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { IMG } from "@/lib/assets";
import { PageBanner } from "@/components/ui/PageBanner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sparkles, Flame, Beef, Wheat, Droplet, Utensils } from "lucide-react";

export default function Nutrition() {
  const qc = useQueryClient();
  const [vitals, setVitals] = useState({ age: "", sex: "male", height_cm: "", weight_kg: "", activity_level: "moderate", goal: "lose fat", diet_pref: "omnivore", allergies: "" });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");

  const { data: savedVitals } = useQuery({ queryKey: ["vitals"], queryFn: async () => (await api.get("/api/vitals")).data });
  const { data: plans = [] } = useQuery({ queryKey: ["diets"], queryFn: async () => (await api.get("/api/nutrition/plans")).data });
  const { data: foods = [] } = useQuery({ queryKey: ["foods", search], queryFn: async () => (await api.get("/api/foods", { params: search ? { search } : {} })).data });

  useEffect(() => { if (savedVitals && savedVitals.goal) setVitals((v) => ({ ...v, ...savedVitals })); }, [savedVitals]);

  const plan = plans[0];
  const set = (k) => (e) => setVitals({ ...vitals, [k]: e?.target ? e.target.value : e });

  const saveVitals = async () => {
    setSaving(true);
    try {
      const payload = { ...vitals, age: Number(vitals.age) || null, height_cm: Number(vitals.height_cm) || null, weight_kg: Number(vitals.weight_kg) || null };
      const { data } = await api.post("/api/vitals", payload);
      toast.success(`Vitals saved · target ${data.tdee} kcal/day`);
      qc.invalidateQueries({ queryKey: ["vitals"] });
    } catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/api/vitals", { ...vitals, age: Number(vitals.age) || null, height_cm: Number(vitals.height_cm) || null, weight_kg: Number(vitals.weight_kg) || null });
      await api.post("/api/nutrition/plan");
      toast.success("OCTON built your diet plan");
      qc.invalidateQueries({ queryKey: ["diets"] });
    } catch (err) { toast.error(apiErr(err)); } finally { setGenerating(false); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6">
      <PageBanner image={IMG.nutrition} overline="fuel" title="Nutrition"
        subtitle="Give OCTON your vitals and it builds a diet plan from scratch, tuned to your goal and macros." />

      <Tabs defaultValue="plan">
        <TabsList className="bg-[#0F1115] border border-[#1E222A]">
          <TabsTrigger value="plan" data-testid="tab-diet">My Diet</TabsTrigger>
          <TabsTrigger value="foods" data-testid="tab-foods">Food Facts</TabsTrigger>
        </TabsList>

        <TabsContent value="plan" className="mt-5 grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Vitals form */}
          <div className="lg:col-span-5 surface rounded-lg p-6 space-y-4">
            <h3 className="font-heading font-bold text-lg tracking-tight">Your vitals</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mono-label">Age</Label><Input type="number" value={vitals.age} onChange={set("age")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="vitals-age" /></div>
              <div>
                <Label className="mono-label">Sex</Label>
                <Select value={vitals.sex} onValueChange={set("sex")}>
                  <SelectTrigger className="mt-1.5 bg-[#0F1115] border-[#1E222A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="surface border-[#1E222A]"><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="mono-label">Height (cm)</Label><Input type="number" value={vitals.height_cm} onChange={set("height_cm")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="vitals-height" /></div>
              <div><Label className="mono-label">Weight (kg)</Label><Input type="number" value={vitals.weight_kg} onChange={set("weight_kg")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="vitals-weight" /></div>
            </div>
            <div>
              <Label className="mono-label">Activity</Label>
              <Select value={vitals.activity_level} onValueChange={set("activity_level")}>
                <SelectTrigger className="mt-1.5 bg-[#0F1115] border-[#1E222A]"><SelectValue /></SelectTrigger>
                <SelectContent className="surface border-[#1E222A]">
                  {["sedentary", "light", "moderate", "active", "athlete"].map((a) => <SelectItem key={a} value={a} className="capitalize">{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mono-label">Goal</Label>
              <Select value={vitals.goal} onValueChange={set("goal")}>
                <SelectTrigger className="mt-1.5 bg-[#0F1115] border-[#1E222A]" data-testid="vitals-goal"><SelectValue /></SelectTrigger>
                <SelectContent className="surface border-[#1E222A]">
                  {["lose fat", "build muscle", "general fitness", "athletic performance", "maintain"].map((g) => <SelectItem key={g} value={g} className="capitalize">{g}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mono-label">Diet</Label>
                <Select value={vitals.diet_pref} onValueChange={set("diet_pref")}>
                  <SelectTrigger className="mt-1.5 bg-[#0F1115] border-[#1E222A]"><SelectValue /></SelectTrigger>
                  <SelectContent className="surface border-[#1E222A]">
                    {["omnivore", "vegetarian", "vegan", "pescatarian", "keto", "halal"].map((d) => <SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="mono-label">Allergies</Label><Input value={vitals.allergies} onChange={set("allergies")} className="mt-1.5 bg-[#0F1115] border-[#1E222A]" placeholder="none" /></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button onClick={saveVitals} disabled={saving} variant="outline" className="border-[#343A4A] bg-transparent hover:bg-white/5 rounded-md flex-1" data-testid="save-vitals-btn">Save vitals</Button>
              <Button onClick={generate} disabled={generating} className="bg-acid text-black hover:bg-[#AACC00] rounded-md flex-1" data-testid="generate-diet-btn">
                <Sparkles className="w-4 h-4" /> {generating ? "Building…" : "Generate diet"}
              </Button>
            </div>
          </div>

          {/* Diet plan */}
          <div className="lg:col-span-7">
            {plan ? (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="surface rounded-lg p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="mono-label text-acid">OCTON diet plan</span>
                    <h3 className="font-heading font-bold text-xl tracking-tight capitalize">{plan.goal}</h3>
                  </div>
                  <div className="text-right">
                    <p className="font-heading font-black text-3xl text-acid">{plan.calories}</p>
                    <p className="mono-label">kcal / day</p>
                  </div>
                </div>
                <p className="text-[#B7BECB] text-sm">{plan.summary}</p>
                <div className="grid grid-cols-3 gap-3">
                  <Macro icon={Beef} label="Protein" value={`${plan.protein_g}g`} color="#CCFF00" />
                  <Macro icon={Wheat} label="Carbs" value={`${plan.carbs_g}g`} color="#FFB340" />
                  <Macro icon={Droplet} label="Fat" value={`${plan.fat_g}g`} color="#0047FF" />
                </div>
                <div className="space-y-3">
                  {(plan.meals || []).map((m, i) => (
                    <motion.div key={m.name} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}
                      className="surface-elev rounded-md p-4" data-testid={`meal-${i}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="font-medium flex items-center gap-2"><Utensils className="w-4 h-4 text-acid" /> {m.name}{m.time ? ` · ${m.time}` : ""}</span>
                        <span className="mono-label flex items-center gap-1"><Flame className="w-3 h-3" />{m.kcal} kcal</span>
                      </div>
                      <p className="text-sm text-[#8A91A6]">{(m.items || []).join(" · ")}</p>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            ) : (
              <div className="surface rounded-lg p-10 h-full flex flex-col items-center justify-center text-center">
                <Sparkles className="w-8 h-8 text-[#565C6F] mb-3" />
                <p className="text-[#8A91A6] max-w-xs">Fill in your vitals and hit <span className="text-acid">Generate diet</span>. OCTON will design a full day of meals tuned to your goal.</p>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="foods" className="mt-5 space-y-4">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search foods…" className="max-w-xs bg-[#0F1115] border-[#1E222A]" data-testid="food-search" />
          <div className="surface rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="border-[#1E222A] hover:bg-transparent">
                  <TableHead className="mono-label">Food (per 100g)</TableHead>
                  <TableHead className="mono-label">Kcal</TableHead>
                  <TableHead className="mono-label">Protein</TableHead>
                  <TableHead className="mono-label hidden md:table-cell">Carbs</TableHead>
                  <TableHead className="mono-label hidden md:table-cell">Fat</TableHead>
                  <TableHead className="mono-label">Group</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {foods.map((f) => (
                  <TableRow key={f.id} className="border-[#1E222A] hover:bg-acid/5" data-testid={`food-${f.name}`}>
                    <TableCell className="font-medium">{f.name}</TableCell>
                    <TableCell className="font-mono2 text-acid">{f.kcal}</TableCell>
                    <TableCell className="font-mono2 text-[#8A91A6]">{f.protein_g}g</TableCell>
                    <TableCell className="font-mono2 text-[#8A91A6] hidden md:table-cell">{f.carbs_g}g</TableCell>
                    <TableCell className="font-mono2 text-[#8A91A6] hidden md:table-cell">{f.fat_g}g</TableCell>
                    <TableCell><span className="text-xs text-bayes">{f.category}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Macro({ icon: Icon, label, value, color }) {
  return (
    <div className="surface-elev rounded-md p-3 text-center">
      <Icon className="w-4 h-4 mx-auto mb-1" style={{ color }} />
      <p className="font-heading font-bold text-lg" style={{ color }}>{value}</p>
      <p className="mono-label">{label}</p>
    </div>
  );
}
